import { useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function PrivacyPage() {
  const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'Privacy Policy | Brushour Painters & Decorators Watford',
    description: 'Read the Brushour privacy policy. Learn how we collect, use, and protect your personal data when you use our painting and decorating services in Watford, Hertfordshire.',
    keywords: 'Brushour privacy policy, data protection painting company Watford',
    canonical: `${SITE_URL}/privacy`,
    schema: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Privacy Policy – Brushour',
        description: 'Privacy policy for Brushour painting and decorating services in Watford, Hertfordshire.',
        url: `${SITE_URL}/privacy`,
        publisher: {
          '@type': 'Organization',
          name: 'Brushour',
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/3d769dc1e01059223eb26668720a1761/e6c157647967ffb5fa62a9e4cb4fe039.png',
          },
        },
      },
    ],
  });

  useEffect(() => {
    // Scroll to top when the component mounts
    try {
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Failed to scroll to top:', error);
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      <main className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-neutral-800 mb-8">Privacy Policy</h1>
          <p className="text-sm text-neutral-500 mb-12">Last updated: January 2025</p>

          <div className="space-y-8 text-neutral-700">
            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">1. Introduction</h2>
              <p className="leading-relaxed">
                Brushour ("we", "our", or "us") is committed to protecting your privacy. This Privacy
                Policy explains how we collect, use, disclose, and safeguard your information when you
                visit our website or use our painting and decorating services in Watford, Hertfordshire
                and surrounding areas.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">2. Information We Collect</h2>
              <p className="leading-relaxed mb-4">
                We may collect information about you in various ways, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Personal Data:</strong> Name, email address, phone number, and postal address
                  when you contact us or request a quote.
                </li>
                <li>
                  <strong>Property Information:</strong> Details about your property relevant to our
                  painting and decorating services.
                </li>
                <li>
                  <strong>Communication Data:</strong> Records of correspondence when you contact us
                  via email, phone, or our website forms.
                </li>
                <li>
                  <strong>Usage Data:</strong> Information about how you use our website, including IP
                  address, browser type, and pages visited.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">3. How We Use Your Information</h2>
              <p className="leading-relaxed mb-4">We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide, operate, and maintain our services</li>
                <li>Respond to your enquiries and provide quotes</li>
                <li>Schedule and complete painting and decorating work</li>
                <li>Send you service-related communications</li>
                <li>Improve our website and services</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">4. Information Sharing</h2>
              <p className="leading-relaxed">
                We do not sell, trade, or rent your personal information to third parties. We may
                share your information only with trusted service providers who assist us in operating
                our business, and only when necessary to provide our services to you.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">5. Data Security</h2>
              <p className="leading-relaxed">
                We implement appropriate technical and organisational measures to protect your
                personal information against unauthorised access, alteration, disclosure, or
                destruction. However, no method of transmission over the Internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">6. Your Rights</h2>
              <p className="leading-relaxed mb-4">
                Under UK data protection law, you have the right to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to processing of your data</li>
                <li>Request data portability</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">7. Cookies</h2>
              <p className="leading-relaxed">
                Our website may use cookies to enhance your browsing experience. You can choose to
                disable cookies through your browser settings, though this may affect some website
                functionality.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-neutral-800 mb-4">8. Contact Us</h2>
              <p className="leading-relaxed">
                If you have any questions about this Privacy Policy or wish to exercise your data
                protection rights, please contact us at:
              </p>
              <div className="mt-4 p-4 bg-beige-50 rounded-lg">
                <p className="font-medium">Brushour</p>
                <p>Watford, Hertfordshire</p>
                <p>Email: daniel@brushour.co.uk</p>
                <p>Phone: 07595037441</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
